/**
 * @fileoverview Demo Engine context — pairs sample data with optional walkthrough launch.
 * When the verification sample workspace is on, it reports every registered scenario as
 * active and serves one shared generated dataset — consumers never branch on the studio
 * flag themselves.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWalkthroughContext } from '@/features/walkthrough/context/WalkthroughContext';
import { useVerificationStudio } from '@/features/verification-studio/VerificationStudioContext';
import { getAllDemoScenarios, getDemoScenario } from './demo-registry';
import type { DemoEngineActions, DemoScenarioId, DemoScenarioSource } from './types';
import './scenarios';

type ScenarioDataMap = Partial<Record<DemoScenarioId, unknown>>;
type ActiveMap = Partial<Record<DemoScenarioId, boolean>>;

interface DemoEngineContextValue extends DemoEngineActions {
  activeScenarios: ActiveMap;
}

const DemoEngineContext = createContext<DemoEngineContextValue | null>(null);

function readStoredActive(storageKey: string): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(storageKey) === 'true';
}

function writeStoredActive(storageKey: string, active: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(storageKey, active ? 'true' : 'false');
}

function readInitialState(): { active: ActiveMap; data: ScenarioDataMap } {
  const active: ActiveMap = {};
  const data: ScenarioDataMap = {};
  getAllDemoScenarios().forEach((def) => {
    if (readStoredActive(def.storageKey)) {
      active[def.id] = true;
      data[def.id] = def.createData();
    }
  });
  return { active, data };
}

export function DemoEngineProvider({ children }: { children: React.ReactNode }) {
  const walkthrough = useWalkthroughContext();
  const { enabled: studio } = useVerificationStudio();

  // Lazy initializer so stored scenarios are active on the first client render —
  // otherwise a reload briefly paints live data.
  const initialRef = useRef<{ active: ActiveMap; data: ScenarioDataMap } | null>(null);
  initialRef.current ??= readInitialState();
  const [activeScenarios, setActiveScenarios] = useState(initialRef.current.active);
  const [scenarioData, setScenarioData] = useState(initialRef.current.data);

  // One shared dataset for the sample workspace, generated lazily per scenario so all
  // surfaces see the same cast (cross-referenced client ids stay consistent).
  const studioDataRef = useRef<ScenarioDataMap>({});

  const enableScenario = useCallback(
    (id: DemoScenarioId, options?: { startWalkthrough?: boolean }) => {
      if (studio) return;
      const def = getDemoScenario(id);
      if (!def) return;

      setActiveScenarios((prev) => ({ ...prev, [id]: true }));
      setScenarioData((prev) => ({ ...prev, [id]: def.createData() }));
      writeStoredActive(def.storageKey, true);

      if (options?.startWalkthrough && def.walkthroughId) {
        walkthrough.startWalkthrough(def.walkthroughId);
      }
    },
    [studio, walkthrough],
  );

  const disableScenario = useCallback(
    (id: DemoScenarioId) => {
      if (studio) return;
      const def = getDemoScenario(id);
      if (!def) return;

      setActiveScenarios((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setScenarioData((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      writeStoredActive(def.storageKey, false);
    },
    [studio],
  );

  const toggleScenario = useCallback(
    (id: DemoScenarioId, options?: { startWalkthrough?: boolean }) => {
      if (isScenarioActiveFor(activeScenarios, studio, id)) {
        disableScenario(id);
      } else {
        enableScenario(id, options);
      }
    },
    [activeScenarios, studio, disableScenario, enableScenario],
  );

  const isScenarioActive = useCallback(
    (id: DemoScenarioId) => isScenarioActiveFor(activeScenarios, studio, id),
    [activeScenarios, studio],
  );

  const getScenarioSource = useCallback(
    (id: DemoScenarioId): DemoScenarioSource => {
      if (studio) return 'studio';
      return activeScenarios[id] ? 'scenario' : null;
    },
    [activeScenarios, studio],
  );

  const getScenarioData = useCallback(
    <TData,>(id: DemoScenarioId): TData | null => {
      if (studio) {
        const def = getDemoScenario(id);
        if (!def) return null;
        studioDataRef.current[id] ??= def.createData();
        return studioDataRef.current[id] as TData;
      }
      return (scenarioData[id] as TData | undefined) ?? null;
    },
    [scenarioData, studio],
  );

  const updateScenarioData = useCallback(
    <TData,>(id: DemoScenarioId, updater: (prev: TData) => TData) => {
      setScenarioData((prev) => {
        const current = prev[id] as TData | undefined;
        if (!current) return prev;
        return { ...prev, [id]: updater(current) };
      });
    },
    [],
  );

  const value = useMemo<DemoEngineContextValue>(
    () => ({
      activeScenarios,
      enableScenario,
      disableScenario,
      toggleScenario,
      isScenarioActive,
      getScenarioSource,
      getScenarioData,
      updateScenarioData,
    }),
    [
      activeScenarios,
      disableScenario,
      enableScenario,
      getScenarioData,
      getScenarioSource,
      isScenarioActive,
      toggleScenario,
      updateScenarioData,
    ],
  );

  return (
    <DemoEngineContext.Provider value={value}>{children}</DemoEngineContext.Provider>
  );
}

function isScenarioActiveFor(
  activeScenarios: ActiveMap,
  studio: boolean,
  id: DemoScenarioId,
): boolean {
  return studio || !!activeScenarios[id];
}

export function useDemoEngineContext(): DemoEngineContextValue {
  const ctx = useContext(DemoEngineContext);
  if (!ctx) {
    throw new Error('useDemoEngineContext must be used within DemoEngineProvider');
  }
  return ctx;
}
